import { loadProductsByPartNumber } from '@/scripts/libs/wcs/frame-genius'
import { WcsFrameAdvisorProduct } from '@/types/frame-genius'
import { RAY_BAN, RAY_BAN_LOGO } from '@/scripts/constants/store'
import Resources from '@/scripts/libs/resources'
import { UserAgent } from '@/scripts/libs/user-agent'
import { Config, Price, ProductPrice } from '@/scripts/libs/virtual-mirror/types/config'
import { Product } from '@/scripts/libs/virtual-mirror/types/product'
import wcs from '@/scripts/libs/wcs'
import PDP from '@/scripts/libs/pdp'
import RbVirtualMirror from '@/elements/virtual-mirror/rb-virtual-mirror.vue'

import makeDebug from 'debug'
import {
  ACTION_SET_VIRTUAL_MIRROR_SCRIPTS_LOADED_STATUS,
  ACTION_SET_VIRTUAL_MIRROR_VISIBILITY,
} from '@/scripts/store/virtual-mirror/actions'
import { Target, VirtualMirrorWidget } from '@/scripts/libs/virtual-mirror/types/vmmv'
import {
  ACTION_ADD_TO_BAG,
  ACTION_ADD_TO_WISHLIST,
  ACTION_REMOVE_FROM_WISHLIST,
} from '@/scripts/store/actions'
import { mapRxcConfig } from '@/scripts/libs/frame-genius'
import { ACTION_SET_RXC_SCRIPTS_STATUS } from '@/scripts/store/actions/pdp'
import { WcsPromotions } from '@/scripts/libs/wcs/promotions'
import { WcsLocales } from '@/scripts/libs/wcs/types/locales'
import { NoEcommerce } from '@/scripts/libs/no-ecommerce'
import Images from '@/scripts/libs/images'
import { CarouselItem } from '@/types/pdp'
import { formatPrice } from '@/scripts/libs/price'
import Vue from 'vue'
import i18n from '@/scripts/libs/i18n'
import VueI18n from 'vue-i18n'
import { WcsStoreId } from '@/scripts/libs/wcs/types/store-id'

const debug = makeDebug('virtual-mirror')

class VirtualMirror {
  private static instance: VirtualMirror
  private static selectorForInjection = '.rb-app__footer'
  private static selector = '.rb-virtual-mirror__vmmv'

  private static target: Target

  private static vmapp: VirtualMirrorWidget
  private static vuexStore: any

  private static i18n: VueI18n

  private static productsFromServer: Array<WcsFrameAdvisorProduct> = []

  static getInstance = (): VirtualMirror => {
    if (!VirtualMirror.instance) {
      VirtualMirror.instance = new VirtualMirror()
    }
    return VirtualMirror.instance
  }

  injectAndInit = async ({
    partNumbers,
    addToCartText,
    target,
    vuexStore,
  }: {
    partNumbers: Array<string>
    addToCartText?: string
    target: Target
    vuexStore: any
  }) => {
    const { virtualMirror } = wcs.getStoreConfig()
    VirtualMirror.vuexStore = vuexStore
    VirtualMirror.target = target
    const vuexState = VirtualMirror.vuexStore.state.virtualMirror

    VirtualMirror.i18n = i18n.setupLocalization()

    if (vuexState.areScriptsLoaded) {
      const isSupported = await this.verifySupport(partNumbers)
      if (isSupported) {
        this.init({
          partNumbers,
          addToCartText: this.getProperAddToCartText(addToCartText, VirtualMirror.target),
        })
      }
    } else {
      VirtualMirror.vuexStore.dispatch(
        `virtualMirror/${ACTION_SET_VIRTUAL_MIRROR_SCRIPTS_LOADED_STATUS}`,
        true
      )
      Resources.loadResourcesWithCallback({
        resources: [virtualMirror.script],
        callback: async () => {
          const isSupported = await this.verifySupport(partNumbers)
          if (isSupported) {
            await this.injectVueComponent()
            await this.init({
              partNumbers,
              addToCartText: this.getProperAddToCartText(addToCartText, VirtualMirror.target),
            })
          }
        },
      })
    }
  }

  async injectVirtualMirrorScript({ vuexStore }: { vuexStore: any }) {
    VirtualMirror.vuexStore = vuexStore
    if (VirtualMirror.vuexStore.state.virtualMirror.areScriptsLoaded) {
      return
    }
    const { virtualMirror } = wcs.getStoreConfig()

    await Resources.loadResourcesWithCallback({ resources: [virtualMirror.script] })
    VirtualMirror.vuexStore.dispatch(
      `virtualMirror/${ACTION_SET_VIRTUAL_MIRROR_SCRIPTS_LOADED_STATUS}`,
      true
    )
  }

  public setUp = ({
    vuexStore,
    setVirtualMirrorScriptsLoadedStatus,
  }: {
    vuexStore: any
    setVirtualMirrorScriptsLoadedStatus: (...args: [boolean]) => void
  }) => {
    return new Promise<void>((resolve) => {
      VirtualMirror.vuexStore = vuexStore
      VirtualMirror.i18n = i18n.setupLocalization()

      const { virtualMirror } = wcs.getStoreConfig()
      const vuexState = VirtualMirror.vuexStore.state.virtualMirror

      if (vuexState.areScriptsLoaded) {
        resolve()
      }
      Resources.loadResourcesWithCallback({
        resources: [virtualMirror.script],
        callback: () => {
          this.injectVueComponent()
          setVirtualMirrorScriptsLoadedStatus(true)
          resolve()
        },
      })
    })
  }

  public init = async ({
    partNumbers,
    addToCartText,
  }: {
    partNumbers: Array<string>
    addToCartText?: string
  }) => {
    const config = await this.getConfig({ partNumbers, addToCartText })

    VirtualMirror.vmapp = new window.vmmv.VMWidgetApp()
    await VirtualMirror.vmapp.setConfig(config)

    VirtualMirror.vuexStore.dispatch(`virtualMirror/${ACTION_SET_VIRTUAL_MIRROR_VISIBILITY}`, true)
    VirtualMirror.vmapp.init()
  }

  private injectVueComponent = async () => {
    const parent = document.querySelector(VirtualMirror.selectorForInjection)

    const i18nInstance = i18n.setupLocalization()
    const VirtualMirrorComponent = Vue.extend(RbVirtualMirror)
    const instance = new VirtualMirrorComponent({
      store: VirtualMirror.vuexStore,
      i18n: i18nInstance,
    })

    instance.$mount()
    parent?.appendChild(instance.$el)
  }

  private verifySupport = async (upcs: string[]): Promise<boolean> => {
    const { virtualMirror } = wcs.getStoreConfig()

    const upcAvailability = await window.vmmv.isUPCSupported(upcs, virtualMirror.key)
    const isBrowserSupported = await window.vmmv.isBrowserSupported()

    return upcAvailability && isBrowserSupported
  }

  public verifySupportForProducts = async (upcs: string[]) => {
    const { virtualMirror } = wcs.getStoreConfig()

    const upcsAvailability = await window.vmmv.isUPCSupported(upcs, virtualMirror.key)
    return upcsAvailability
  }

  public isBrowserSupported = async () => {
    const isBrowserSupported = await window.vmmv.isBrowserSupported()
    return isBrowserSupported
  }

  private getProperAddToCartText(addToCartText: string | undefined, target: Target) {
    if (target === 'pdp') {
      const shopNow = VirtualMirror.vuexStore.getters.translation('common.shop_now')
      return shopNow
    }

    if (addToCartText) {
      return addToCartText
    }

    return undefined
  }

  private filterAndMapProducts = (
    productsFromServer: Array<WcsFrameAdvisorProduct>,
    selectedUpc: string,
    partNumbers: Array<string>
  ): Product[] => {
    const partNumbersOrder = [...partNumbers]
    return productsFromServer
      .map((product) => {
        const [styleName] = product?.productName?.split(' ')
        return {
          upc: product.upc,
          code: product.model,
          name: product.name,
          colorCode: product.moco,
          thumbnailUrl: Images.getVirtualMirrorProductImage(product.imageUrl),
          brand: {
            name: RAY_BAN,
            logoUrl: RAY_BAN_LOGO,
          },
          lensColorLabel: product.lensColor,
          frameColorLabel: product.frameColor,
          styleName,
          category: product.category,
          isCustom: false,
          size: product.size,
          isTransition: false,
          rxAvailable: VirtualMirror.target === 'pdp' ? false : product.rxAvailable,
          isSelected: selectedUpc === product.upc,
        }
      })
      .sort((a, b) => {
        const indexA = partNumbersOrder.indexOf(a.upc)
        const indexB = partNumbersOrder.indexOf(b.upc)
        return indexA - indexB
      })
  }

  private mapPrices = (products: Array<Product>): Array<ProductPrice> => {
    return products.map((product) => {
      const productFromServer = VirtualMirror.productsFromServer.find(
        (productFromServer) => productFromServer?.upc === product?.upc
      )
      if (productFromServer) {
        const currentAndPrevious = this.mapCurrentAndPreviousPrices(productFromServer)
        return {
          upc: product.upc,
          currency: productFromServer?.prices?.currency,
          ...currentAndPrevious,
        }
      }
      return {
        upc: product.upc,
        currency: '',
        current: {
          text: '0',
          value: 0,
        },
      }
    })
  }

  private mapCurrentAndPreviousPrices = (
    productFromServer: WcsFrameAdvisorProduct
  ): {
    current: Price
    previous?: Price
  } => {
    const originalPrice = {
      text: productFromServer.prices.listPrice,
      value: +productFromServer.prices.listPrice,
    }

    const currentPromotion = WcsPromotions.calculatePromotionInProgress(
      productFromServer.promotions
    )
    if (currentPromotion.price) {
      const formattedDiscountedPrice = formatPrice(
        currentPromotion.price,
        productFromServer.prices.priceFormat,
        productFromServer.prices.currencySymbol
      )
      const formattedDiscountedPriceWithoutCurrency = formattedDiscountedPrice.replace(
        productFromServer.prices.currencySymbol,
        ''
      )
      return {
        current: {
          text: `${formattedDiscountedPriceWithoutCurrency}`,
          value: currentPromotion.price,
        },
        previous: originalPrice,
      }
    }
    return {
      current: originalPrice,
    }
  }

  private getConfig = async ({
    partNumbers,
    addToCartText,
  }: {
    partNumbers: Array<string>
    addToCartText?: string
  }): Promise<Config> => {
    VirtualMirror.productsFromServer = await loadProductsByPartNumber(partNumbers)
    const selectedUpc = this.evaluateSelectedUpc()
    const products = this.filterAndMapProducts(
      VirtualMirror.productsFromServer,
      selectedUpc,
      partNumbers
    )
    const config = this.mapConfig()

    config.products = products
    config.transitionFeature = { enabled: false }
    config.addToCartText = addToCartText
    config.alternativeDecisionPath = this.mapAlternativeDecisionPathLabels()
    config.onToggleWishlist = this.onToggleWishlist
    config.wishlistedUpcs = this.getWishlistedUpcs()
    const addToBagIsEnabled = this.addToBagIsEnabled()

    if (!NoEcommerce.isStoreNoEcommerce() && addToBagIsEnabled) {
      config.onAlternativeDecisionPath = this.onAlternativeDecisionPath
      config.onAddToBag = this.onAddToBag
      config.prices = this.mapPrices(products)
    }

    return config
  }

  mapConfig = (): Config => {
    const { virtualMirror, locale, storeId } = wcs.getStoreConfig()

    return {
      selector: VirtualMirror.selector,
      locale: this.adjustLocale(locale, storeId),
      store: {
        id: '',
        type: 'rb',
        name: RAY_BAN,
        standAlone: true,
        logo: RAY_BAN_LOGO,
        region: virtualMirror.region,
        countryId: '',
        globalStoreId: '',
      },
      style: 'rb',
      fromStore: true,
      vmInit: {
        source: 'VMMV_RB',
        key: virtualMirror.key,
        channel: 'ecommerce',
        brand: 'RB',
      },
      isPictureModeEnabled: virtualMirror.isPictureModeEnabled,
      isUploadPictureEnabled: virtualMirror.isUploadPictureEnabled,
      isTakePictureEnabled: virtualMirror.isTakePictureEnabled,
      isTryOnEnabled: virtualMirror.isTryOnEnabled,
      isVideoModeEnabled: virtualMirror.isVideoModeEnabled,
      isQrCodeEnabled: virtualMirror.isQrCodeEnabled,
      isTakeScreenshotEnabled: virtualMirror.isTakeScreenshotEnabled,
      disableSavePolaroid: virtualMirror.disableSavePolaroid,
      onClose: this.onClose,
      onShare: this.onShare,
      onPrivacyReject: this.onClose,
      termsAndConditions: WcsLocales.getTermsAndConditionsLink(locale),
    }
  }

  private mapAlternativeDecisionPathLabels() {
    const cta = `${VirtualMirror.i18n.t('virtualMirror.alternativeDecisionPath.cta')}`
    const text = `${VirtualMirror.i18n.t('virtualMirror.alternativeDecisionPath.text')}`
    const title = `${VirtualMirror.i18n.t('virtualMirror.alternativeDecisionPath.title')}`
    if (!cta || !text || !title) {
      return undefined
    }
    return {
      cta,
      text,
      title,
    }
  }

  private adjustLocale(locale: string, storeId: string) {
    if (storeId === WcsStoreId.REST_OF_EUROPE) {
      return 'en-EU'
    }
    return locale
  }

  private addToBagIsEnabled() {
    const { virtualMirror } = wcs.getStoreConfig()
    return VirtualMirror.productsFromServer.every(
      (product) => !virtualMirror.categoriesWithoutAddToBag.includes(product.category)
    )
  }

  private evaluateSelectedUpc = () => {
    if (wcs.isInPdp()) {
      const selectedCatEntryId = VirtualMirror.vuexStore.getters['pdp/selectedCatEntryId']

      const carouselItem = VirtualMirror.vuexStore.getters['pdp/carousel']?.find(
        (item: CarouselItem) => item.correlatedCatEntryIds.includes(selectedCatEntryId)
      )
      const selectedProductUpc = VirtualMirror.vuexStore.getters['pdp/selectedProduct'].partNumber

      return selectedProductUpc ?? ''
    }

    return VirtualMirror.productsFromServer[0].upc
  }

  private onAlternativeDecisionPath = async (product: Product) => {
    const { roxLensPanel } = wcs.getStoreConfig()
    if (!roxLensPanel.isEnabled) {
      return
    }
    const rxcProduct = VirtualMirror.productsFromServer.find((item) => item.upc === product.upc)
    const ava = (await PDP.getMoCoAvailability(rxcProduct?.model)) as any
    const isAvailable = ava?.data[rxcProduct?.moco || '']?.availability || false
    if (rxcProduct && isAvailable) {
      const configObject = mapRxcConfig(rxcProduct)

      if (!VirtualMirror.vuexStore.state['pdp/areRxcScriptsLoaded']) {
        Resources.loadResourcesWithCallback({
          resources: [roxLensPanel.rxc],
          callback: () => {
            VirtualMirror.vuexStore.dispatch(`pdp/${ACTION_SET_RXC_SCRIPTS_STATUS}`, true)
            window.triggerVueMethod('showRoxPanel', configObject)
          },
        })
      } else {
        window.triggerVueMethod('showRoxPanel', configObject)
      }
    }
  }

  private onClose = async () => {
    await VirtualMirror.vmapp.unmount()
    VirtualMirror.vuexStore.dispatch(`virtualMirror/${ACTION_SET_VIRTUAL_MIRROR_VISIBILITY}`, false)
  }

  private onAddToBag = async (product: Product) => {
    const productFromServer = VirtualMirror.productsFromServer.find(
      (productFromServer) => productFromServer.upc === product.upc
    )
    if (!productFromServer) {
      return
    }

    if (!VirtualMirror.target) {
      await this.addToBag(productFromServer.catEntryId)
    } else if (VirtualMirror.target === 'pdp') {
      window.location.href = productFromServer.url
    } else if (VirtualMirror.target === 'cart') {
      await this.addToBag(productFromServer.catEntryId)
      const cartUrl = wcs.getEndPoint('shopCartView')
      window.location.href = cartUrl
    }
  }

  private async addToBag(catEntryId: string) {
    await VirtualMirror.vuexStore.dispatch(ACTION_ADD_TO_BAG, {
      catEntry: catEntryId,
      isPickupStore: false,
      isMobile: false,
      redirect: false,
    })
  }

  private onToggleWishlist = async (product: Product) => {
    const productFromServer = VirtualMirror.productsFromServer.find(
      (productFromServer) => productFromServer.upc === product.upc
    )
    if (productFromServer) {
      const isProductInsideWishlist = VirtualMirror.vuexStore.getters.wishlistItems.find(
        (item: { catentryId: string }) => item.catentryId === productFromServer.catEntryId
      )
      if (isProductInsideWishlist) {
        VirtualMirror.vuexStore.dispatch(ACTION_REMOVE_FROM_WISHLIST, {
          wishlistItemId: productFromServer.catEntryId,
        })
      } else {
        VirtualMirror.vuexStore.dispatch(ACTION_ADD_TO_WISHLIST, {
          catEntry: productFromServer.catEntryId,
        })
      }
    }
  }

  onShare = async (imageDataUrl: string, product: Product) => {
    try {
      const response = await fetch(imageDataUrl)

      if (!response.ok) {
        throw new Error('Failed to fetch image')
      }

      const blob = await response.blob()
      const file = new File([blob], `${product.upc}.jpg`, { type: blob.type })

      const shareOptions = this.getShareOptions(file)

      if (navigator.share) {
        // Check if the Web Share API is available
        await navigator.share(shareOptions)
        return 'Shared successfully'
      } else {
        // Fallback for browsers that don't support Web Share API
        // Implement your custom sharing solution here if needed
        return 'Web Share API is not supported in this browser'
      }
    } catch (error) {
      console.error('Error sharing image:', error)
      // Handle the error appropriately and return an error message or throw it further.
      return 'Failed to share image'
    }
  }

  private getShareOptions(file: File): ShareData {
    if (UserAgent.osIsIOS || UserAgent.browserIsSafari) {
      return {
        files: [file],
      }
    }
    return {
      title: 'Hello',
      text: 'Check out this image!',
      files: [file],
    }
  }

  private getWishlistedUpcs = () => {
    return VirtualMirror.vuexStore.getters.wishlistItems.map(
      (item: { partnumber: string }) => item.partnumber
    )
  }

  isSupportedByBrowser = (): boolean => {
    if (UserAgent.browserIsInternetExplorer) {
      return false
    }

    const { browsers, browserVersions } = wcs.getStoreConfig()

    const browserName = UserAgent.browserName
    const isBrowserSupported = !!browserName && browsers?.includes(browserName)
    if (!isBrowserSupported) {
      return false
    }

    if (
      isNaN(UserAgent.browserVersion) ||
      UserAgent.browserVersion < browserVersions[browserName]
    ) {
      return false
    }

    if (UserAgent.osIsIOS || UserAgent.browserIsSafari) {
      debug('The browser %s is supported: %s', browserName, isBrowserSupported)
      return true
    }

    return false
  }
}

const virtualMirror = VirtualMirror.getInstance()
export default virtualMirror
